import {
  AlertTriangle, Check, Clock, CornerDownRight, Download, Edit, ExternalLink, User as UserIcon, Users, X,
} from 'react-feather';
import { DocumentNode } from 'graphql';
import { ReactNode, useState } from 'react';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { differenceInMinutes } from 'date-fns';
import styled, { css } from 'styled-components';

import { LotteryStatus, ProductType, RegistrationStatus } from '__generated__/graphql';

import { BasicRegistration, RegistrationSummary } from '../helpers';
import { DropdownSection } from '../../Common/Layout';
import { TicketLabel } from '../../../common/Common/SellableLabel';
import { assetUrl, countItems, guessCountry, mergeUrlParams } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import { useParticipant } from '../ParticipantProvider';
import ConfirmLotteryForm from './ConfirmLotteryForm';
import EditRegistrationForm from './EditRegistrationForm';
import ExchangeInfo from './ExchangeInfo';
import GetRegistrationDetailsQuery from './GetRegistrationDetailsQuery';
import LeaveWaitlistForm from './LeaveWaitlistForm';
import ParticipantFieldValue from '../../../common/ParticipantFields/ParticipantFieldValue';
import RegistrationAttribute from '../../../common/Registrations/RegistrationAttribute';
import ResaleForm from './ResaleForm';
import RespondToWaitlistInviteForm from './RespondToWaitlistInviteForm';
import TransferForm from './TransferForm';
import UI from '../../../common/UI';
import UpgradeDetails from './UpgradeDetails';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useResaleState from '../../../common/useResaleState';
import useScroll from '../../../common/useScroll';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';

export interface RegistrationDetailsProps {
  registration: BasicRegistration;
  summary: RegistrationSummary;
  action?: Action | null;
  refetchQueries?: DocumentNode[];
}

const RegistrationDetails = ({
  registration,
  summary,
  action: initialAction = null,
  refetchQueries,
}: RegistrationDetailsProps) => {
  const { t } = useTranslation();

  const containerId = `Registration_${registration.id}`;

  const [action, setAction] = useState<Action | null>(initialAction);

  const isOpen = !!action;

  return (
    <DropdownSection
      open={isOpen}
      onToggle={() => setAction((action) => (action ? null : 'details'))}
      title={`${t('ticket')} ATL${registration.sequence}`}
      id={containerId}
      sc={{ pr: isOpen ? [3, 4] : [2, 3] }}
      preview={(
        <RegistrationTitle
          registration={registration}
          summary={summary}
          activeAction={action}
          setAction={(newAction) => setAction(newAction)}
        />
      )}
      body={(
        <RegistrationBody
          registrationId={registration.id}
          summary={summary}
          containerId={containerId}
          action={action}
          setAction={setAction}
          refetchQueries={refetchQueries}
        />
      )}
    />
  );
};

// eslint-disable-next-line max-len
type Action = 'details' | 'edit_registration' | 'exchange_ticket' | 'assign_ticket' | 'reassign_ticket' | 'resell_ticket' | 'confirm_lottery' | 'respond_to_waitlist_invite' | 'leave_waitlist';

interface RegistrationBodyProps {
  registrationId: string;
  summary: RegistrationSummary;
  containerId: string;
  action: Action;
  setAction: (action: Action) => void;
  refetchQueries?: DocumentNode[];
}

const RegistrationBody = ({
  registrationId,
  summary,
  containerId,
  action,
  setAction,
  refetchQueries,
}: RegistrationBodyProps) => {
  const { t } = useTranslation();
  const { participant } = useParticipant();
  const { formatDate, parseDate } = useLocale();
  const { scrollToIfInvisible, scrollTo } = useScroll();
  const notifier = useNotifier();
  const project = useProject();
  const formatTimeSlot = useTimeSlotFormatter();

  const activeAction = action;

  const { data } = useQuery(
    GetRegistrationDetailsQuery,
    {
      variables: {
        participant: summary.participant.id,
        event: summary.event.id,
        token: summary.view_token,
        registration: registrationId,
      },
    },
  );

  const registration = data?.registrationSummary.registration;

  if (!registration) {
    return (
      <UI.Div sc={{ padding: [3, 4] }}>
        <UI.PageLoader />
      </UI.Div>
    );
  }

  const participants = participant && (registration.participant.id !== participant.id || !registration.assigned)
    ? [participant]
    : [];

  const visibleUpgrades = registration.upgrades.filter((upgrade) => (
    upgrade.product.type === ProductType.item
  ));

  const defaultCountry = guessCountry(project);

  const candidates = participants.map((participant) => ({
    participant: {
      email: participant.email,
      first_name: participant.first_name,
      last_name: participant.last_name,
    },
    details: {
      date_of_birth: participant.date_of_birth,
      gender: participant.gender,
      nationality: participant.nationality || defaultCountry,
      street: participant.street,
      house_number: participant.house_number,
      zip_code: participant.zip_code,
      city: participant.city,
      extra_address_line: participant.extra_address_line,
      country: participant.country || defaultCountry,
      phone: participant.phone,
      emergency_phone: participant.emergency_phone,
    },
  }));

  const isLocked = registration.locked;
  const isOwner = registration.owner.id === summary.participant.id;

  const handleOpenEditRegistrationForm = () => {
    setAction('edit_registration');
    scrollToIfInvisible(`Registration_${registration.id}`, { gutter: 1 });
  };

  const handleEdit = () => {
    setAction('details');
    scrollToIfInvisible(containerId, { gutter: 1 });
    notifier.success(t('information_saved'));
  };

  const handleRejectWaitlistInvitation = () => {
    setAction(null);
    scrollToIfInvisible(containerId, { gutter: 1 });
    notifier.success(t('ticket_rejected'));
  };

  const handleCancel = () => {
    setAction('details');
  };

  return (
    <UI.Div>
      <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between', padding: [3, 4] }}>
        <UI.H4>
          {getLongActionTitle(activeAction, registration, t)}
        </UI.H4>

        {action === 'details' && !isLocked && (
          <UI.Button
            sc={{
              size: 'sm', blank: true, my: -1, px: 1,
            }}
            onClick={() => setAction('edit_registration')}
          >
            <UI.Icon>
              <Edit />
            </UI.Icon>
            {' '}
            {t('edit_details')}
          </UI.Button>
        )}
        {action === 'edit_registration' && (
          <UI.Button
            sc={{
              size: 'sm', blank: true, my: -1, px: 1, fontSize: 3,
            }}
            onClick={() => setAction('details')}
            title={t('close')}
          >
            <UI.Icon>
              <X />
            </UI.Icon>
          </UI.Button>
        )}
      </UI.FlexContainer>
      <UI.HR sc={{ mx: [3, 4], my: 0 }} style={{ borderTopWidth: 2 }} />
      {activeAction === 'details' && (
        <UI.Div sc={{ padding: [3, 4] }}>
          <UI.Delimit delimiter={<UI.HR sc={{ dashed: true }} />}>
            {(registration.time_slot || registration.ticket.upcoming_time_slots.length > 0) && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('time_slot')}
                </UI.Legend>
                {registration.time_slot && (
                  <>
                    <UI.Icon>
                      <Clock />
                    </UI.Icon>
                    {' '}
                    <UI.Delimit>
                      {formatTimeSlot(registration.time_slot)}
                      {registration.time_slot.title}
                    </UI.Delimit>
                  </>
                )}
                {!registration.time_slot && registration.ticket.upcoming_time_slots.length > 0 && (
                  <UI.Span sc={{ color: 'error' }}>
                    <UI.Icon>
                      <AlertTriangle />
                    </UI.Icon>
                    {' '}
                    {t('no_time_slot_chosen')}
                  </UI.Span>
                )}
              </UI.Div>
            )}

            {summary.event.ticket_numbers_published && registration.ticket_number && (
              <UI.Div>
                <UI.Div>
                  <UI.Legend sc={{ fontWeight: 500, mb: 1 }}>
                    {t('ticket_number')}
                  </UI.Legend>
                  <UI.Label sc={{ fontWeight: 500, fontSize: 3 }}>
                    <UI.Icon>
                      <UI.Icons.StartFinish />
                    </UI.Icon>
                    {' '}
                    {registration.ticket_number}
                  </UI.Label>
                </UI.Div>
                {(registration.start_time || registration.corral_name) && (
                  <UI.Div sc={{ mt: 1 }}>
                    <UI.Delimit>
                      {registration.corral_name}
                      {registration.start_time && formatDate(
                        parseDate(registration.start_time, { format: 'internal_time' }),
                        { format: 'display_time' },
                      )}
                    </UI.Delimit>
                  </UI.Div>
                )}
              </UI.Div>
            )}

            {(registration.ticket.allow_create_team || registration.ticket.allow_join_team) && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('team')}
                </UI.Legend>

                {registration.team && (
                  <>
                    <UI.Icon>
                      <Users />
                    </UI.Icon>
                    {' '}
                    <UI.Delimit>
                      {registration.team.title}
                      {registration.team.captain.id === summary.participant.id && (
                        <UI.Link
                          to={mergeUrlParams({
                            teams: { id: registration.team.id, search: registration.team.title },
                          })}
                          onClick={() => scrollTo('TeamsSection', { gutter: 1 })}
                        >
                          {t('view_team')}
                        </UI.Link>
                      )}
                    </UI.Delimit>
                  </>
                )}
                {!registration.team && (
                  <UI.Span sc={{ muted: true }}>
                    {t('no_team')}
                  </UI.Span>
                )}
              </UI.Div>
            )}

            {!registration.team && registration.group_members.length > 1 && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('group')}
                </UI.Legend>

                <UI.OL>
                  {registration.group_members.map((member) => (
                    <UI.LI sc={{ muted: !member.full_name }} key={member.id}>
                      {member.full_name || t('not_assigned_yet')}
                    </UI.LI>
                  ))}
                </UI.OL>
              </UI.Div>
            )}

            <UI.GridContainer>
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>{`${t('email_address')}`}</UI.Legend>
                {registration.participant.email}
              </UI.Div>

              {registration.required_attributes.map((attribute) => (
                <RegistrationAttribute
                  registration={registration}
                  attribute={attribute}
                  showEmptyWarning
                  key={attribute}
                />
              ))}
            </UI.GridContainer>

            {registration.participant_fields.length > 0 && (
              <UI.GridContainer>
                {registration.participant_fields.map((field) => (
                  <ParticipantFieldValue
                    fieldEntry={{
                      value: null,
                      choice_entries: [],
                      field: {
                        id: field.id,
                      },
                      ...registration.participant_field_entries.filter(
                        (fieldEntry) => fieldEntry.field.id === field.id,
                      )[0],
                    }}
                    field={field}
                    key={field.id}
                  />
                ))}
              </UI.GridContainer>
            )}

            {!isOwner && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('ticket_owner')}
                </UI.Legend>
                <UI.Div>
                  <UI.Icon>
                    <UserIcon />
                  </UI.Icon>
                  {' '}
                  {registration.owner_name}
                </UI.Div>
              </UI.Div>
            )}

            {visibleUpgrades.length > 0 && (
              <UI.Delimit delimiter={<UI.HR sc={{ dashed: true }} />}>
                {visibleUpgrades.map((upgrade) => (
                  <UpgradeDetails upgrade={upgrade} key={upgrade.id} />
                ))}
              </UI.Delimit>
            )}

            {registration.fundraising_campaign && (
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('fundraiser')}
                </UI.Legend>
                <UI.Div>
                  <UI.A
                    href={registration.fundraising_campaign.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {registration.charity?.title}
                    {' '}
                    <UI.Icon>
                      <ExternalLink />
                    </UI.Icon>
                  </UI.A>
                </UI.Div>
              </UI.Div>
            )}
          </UI.Delimit>
        </UI.Div>
      )}
      {activeAction === 'edit_registration' && (
        <EditRegistrationForm
          registration={registration}
          event={summary.event}
          allowReassign={registration.is_reassignable && isOwner}
          allowUnassign={registration.is_unassignable && isOwner}
          candidates={registration.is_reassignable ? candidates : []}
          onSuccess={() => handleEdit()}
          onCancel={() => handleCancel()}
          refetchQueries={refetchQueries}
        />
      )}
      {activeAction === 'exchange_ticket' && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <ExchangeInfo registration={registration} event={summary.event} />
        </OwnerAction>
      )}
      {(activeAction === 'assign_ticket' || activeAction === 'reassign_ticket') && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <TransferForm
            registration={registration}
            event={summary.event}
            openEditRegistrationTab={() => handleOpenEditRegistrationForm()}
          />
        </OwnerAction>
      )}
      {(activeAction === 'resell_ticket') && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <ResaleForm registration={registration} event={summary.event} />
        </OwnerAction>
      )}
      {activeAction === 'confirm_lottery' && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <ConfirmLotteryForm
            registration={registration}
            event={summary.event}
          />
        </OwnerAction>
      )}
      {activeAction === 'respond_to_waitlist_invite' && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <RespondToWaitlistInviteForm
            registration={registration}
            event={summary.event}
            onReject={handleRejectWaitlistInvitation}
          />
        </OwnerAction>
      )}
      {activeAction === 'leave_waitlist' && (
        <OwnerAction isOwner={isOwner} owner={registration.owner_name}>
          <LeaveWaitlistForm
            registration={registration}
          />
        </OwnerAction>
      )}
    </UI.Div>
  );
};

interface OwnerActionProps {
  isOwner: boolean;
  owner: string;
  children?: ReactNode;
}

const OwnerAction = ({ isOwner, owner, children }: OwnerActionProps) => {
  const { t } = useTranslation();

  if (!isOwner) {
    return (
      <UI.Div sc={{ padding: [3, 4] }}>
        <UI.Warning>
          {t('action_only_available_for_owner_warning', { owner })}
        </UI.Warning>
      </UI.Div>
    );
  }

  return <>{children}</>;
};

export interface RegistrationTitleProps {
  registration: BasicRegistration;
  summary: RegistrationSummary;
  activeAction: Action | null;
  setAction: (action: Action | null) => void;
}

const RegistrationTitle = ({
  registration, summary, activeAction, setAction,
}: RegistrationTitleProps) => {
  const { t } = useTranslation();
  const { formatNumber, parseDate, formatDate } = useLocale();
  const formatTimeSlot = useTimeSlotFormatter();

  const { publicResale } = useResaleState(summary.event);

  const hasTicketNumber = summary.event.ticket_numbers_published && registration.ticket_number;

  const upgrades = countItems(registration.upgrades.filter(
    (upgrade) => upgrade.product.type === ProductType.item,
  ).map((upgrade) => (
    `${upgrade.product.title}${upgrade.product_variant ? ` (${upgrade.product_variant.title})` : ''}`
  )));

  const isOwner = registration.owner.id === summary.participant.id;
  const isMainTicket = registration.purchase.unit_id === null || registration.purchase.unit_id === 1;
  const isExpanded = !!activeAction;
  const { preregistration } = registration;
  const waitlistInvite = registration.waitlist_invite;

  const actions: Action[] = [
    'details',
    ...registration.is_reassignable && !registration.assigned ? ['assign_ticket'] as const : [],
    ...registration.is_reassignable && registration.assigned ? ['reassign_ticket'] as const : [],
    ...isMainTicket && registration.exchange_code && summary.event.active ? ['exchange_ticket'] as const : [],
    ...isMainTicket && registration.resale.resellable && summary.event.active ? ['resell_ticket'] as const : [],
    ...registration.waitlist_position && !waitlistInvite?.pending_invite ? ['leave_waitlist'] as const : [],
  ];

  const primaryActions: Action[] = ['assign_ticket'];

  const minutesLeftForLotteryConfirmation = preregistration?.draw_result
    ? Math.max(
      differenceInMinutes(parseDate(preregistration.draw_result.end_date, { timezone: 'UTC' }), new Date()),
      0,
    )
    : 0;
  const hoursLeftForLotteryConfirmation = Math.round(minutesLeftForLotteryConfirmation / 60);
  const daysLeftForLotteryConfirmation = Math.round(hoursLeftForLotteryConfirmation / 24);

  const minutesLeftForWaitlistInviteReply = waitlistInvite?.pending_invite
    ? Math.max(
      differenceInMinutes(parseDate(waitlistInvite.pending_invite.expires_at, { timezone: 'UTC' }), new Date()),
      0,
    )
    : 0;
  const hoursLeftForWaitlistInviteReply = Math.round(minutesLeftForWaitlistInviteReply / 60);
  const daysLeftForWaitlistInviteReply = Math.round(hoursLeftForWaitlistInviteReply / 24);

  return (
    <UI.Div>
      {registration.qr_url && (
        <UI.Div
          sc={{ textAlign: 'center' }}
          style={{
            transition: 'all 0.15s ease-in-out',
            height: isExpanded ? 211 : 0,
            opacity: isExpanded ? 1 : 0,
            overflow: 'hidden',
          }}
        >
          <DownloadTicketButton
            href={registration.ticket_url}
            target="_blank"
            rel="noopener"
            sc={{
              disabled: !summary.event.tickets_available,
            }}
          >
            <img
              src={`${registration.qr_url}&size=300`}
              width="100%"
              height="100%"
              alt={t('qr_code')}
              style={{ display: 'block', opacity: 0.8 }}
            />
            <UI.Div sc={{ mt: 0.5, fontWeight: 500, small: true }}>
              <UI.Icon>
                <Download />
              </UI.Icon>
              {' '}
              {t('download_pdf')}
            </UI.Div>
          </DownloadTicketButton>
          <UI.HR sc={{ dashed: true, my: 3 }} />
        </UI.Div>
      )}
      <UI.FlexContainer sc={{ justifyContent: 'space-between', pr: 5 }}>
        <UI.Div>
          <UI.H4
            sc={{
              color: !registration.assigned ? 'gray.400' : undefined,
              fontSize: 3,
              fontWeight: 600,
            }}
          >
            {registration.assigned
              ? registration.full_name
              : (registration.invited_email || t('not_assigned_yet'))}
            {!registration.assigned && registration.invited_email && ` ${t('invited_parentheses')} `}
          </UI.H4>
          <UI.FlexContainer sc={{ wrap: 'wrap', mr: -1 }}>
            <TicketLabel status={registration.status} sc={{ mt: 0.5, mr: 1 }} style={{ minWidth: 0 }}>
              {registration.ticket.title}
              {isOwner && registration.purchase.batch_size > 1 && (
                <UI.Span style={{ fontWeight: 300, opacity: 0.7 }}>
                  {` (${formatNumber(registration.purchase.unit_id)}/${formatNumber(registration.purchase.batch_size)})`}
                </UI.Span>
              )}
            </TicketLabel>
            {preregistration?.lottery_status === LotteryStatus.pending && (
              <UI.Label sc={{ small: true, mt: 0.5, mr: 1 }}>
                <UI.Icon>
                  <UI.Icons.Pending />
                </UI.Icon>
                {' '}
                {t('pending')}
              </UI.Label>
            )}
            {registration.preregistered_by && (
              <UI.Label sc={{ small: true, mt: 0.5, mr: 1 }}>
                <UI.Icon>
                  <Check />
                </UI.Icon>
                {' '}
                {t('confirmed')}
              </UI.Label>
            )}
            {publicResale.enabled
              && registration.status === RegistrationStatus.waitlist
              && !registration.waitlist_invite?.pending_invite
              && registration.waitlist_position > 0 && (
              <UI.Label sc={{ small: true, mt: 0.5, mr: 0.5 }}>
                <UI.Icon>
                  <UI.Icons.Hourglass />
                </UI.Icon>
                {' '}
                {registration.waitlist_position <= 10
                  ? t('position_lte_x', { x: formatNumber(registration.waitlist_position) })
                  : t('position_x', { x: formatNumber(registration.waitlist_position) })}
              </UI.Label>
            )}
            {registration.time_slot && (
              <UI.Label sc={{ small: true, mt: 0.5, mr: 0.5 }}>
                <UI.Icon>
                  <Clock />
                </UI.Icon>
                {' '}
                {formatTimeSlot(registration.time_slot)}
              </UI.Label>
            )}
            {hasTicketNumber && (
              <UI.Label sc={{ small: true, mt: 0.5 }}>
                <UI.Icon>
                  <UI.Icons.StartFinish />
                </UI.Icon>
                {' '}
                {registration.ticket_number}
              </UI.Label>
            )}
          </UI.FlexContainer>
        </UI.Div>
        {registration.qr_url && (
          <UI.Div
            sc={{ ml: 2 }}
            style={{
              width: 46,
              opacity: isExpanded ? 0 : 1,
              transition: 'opacity 0.15s ease-in-out',
            }}
          >
            <UI.Div
              style={{
                padding: 4,
                border: '1px solid rgba(0, 0, 0, .15)',
                borderRadius: 3,
                width: 46,
                height: 46,
              }}
            >
              <img
                src={`${registration.qr_url}&size=300`}
                width="100%"
                height="100%"
                alt={t('qr_code')}
                style={{ display: 'block', opacity: registration.assigned ? 0.8 : 0.5 }}
              />
            </UI.Div>
          </UI.Div>
        )}
      </UI.FlexContainer>
      {(registration.team || upgrades.length > 0) && (
        <>
          <UI.HR sc={{ dashed: true, my: isExpanded ? 3 : 2 }} />
          <UI.Div sc={{ color: 'gray.500', small: true }}>
            {registration.team && (
              <UI.Span sc={{ mr: 2 }}>
                <UI.Icon>
                  <Users />
                </UI.Icon>
                {' '}
                {registration.team.title}
              </UI.Span>
            )}
            {upgrades.map((upgrade) => (
              <UI.Span sc={{ mr: 2 }} key={upgrade}>
                <UI.Icon>
                  <CornerDownRight />
                </UI.Icon>
                {' '}
                {upgrade}
              </UI.Span>
            ))}
          </UI.Div>
        </>
      )}
      {summary.event.active && publicResale.enabled && registration.ticket.allow_resale
        && registration.resale.public && registration.resale.available && (
        <UI.Info sc={{ relative: true, mt: 2 }} style={{ zIndex: 300 }}>
          <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <UI.Span>
              {registration.purchase.batch_size > 1 ? (
                <>
                  {t('ticket_for_sale_in_group_message')}
                  <UI.Span style={{ fontWeight: 300, opacity: 0.7 }}>
                    {` (${formatNumber(registration.purchase.unit_id)}/${formatNumber(registration.purchase.batch_size)})`}
                  </UI.Span>
                </>
              ) : t('ticket_for_sale_message')}
            </UI.Span>
            {isOwner && registration.resale.resellable && (
              <UI.Div sc={{ padding: 1, margin: -1, relative: true }} style={{ zIndex: 300 }}>
                <UI.Button
                  onClick={() => setAction('resell_ticket')}
                  sc={{ brand: 'secondary', size: 'sm', active: activeAction === 'resell_ticket' }}
                >
                  {t('edit')}
                </UI.Button>
              </UI.Div>
            )}
          </UI.FlexContainer>
        </UI.Info>
      )}
      {summary.event.active && publicResale.enabled && registration.ticket.allow_resale
        && registration.resale.public && !registration.resale.available && (
        <UI.Warning sc={{ relative: true, mt: 2 }} style={{ zIndex: 300 }}>
          {registration.purchase.batch_size > 1 ? (
            <>
              {registration.resale.has_pending_waitlist_invitation
                ? t('waitlist_invitation_sent_in_group_warning')
                : t('resale_in_progress_in_group_warning')}
              <UI.Span style={{ fontWeight: 300, opacity: 0.7 }}>
                {` (${formatNumber(registration.purchase.unit_id)}/${formatNumber(registration.purchase.batch_size)})`}
              </UI.Span>
            </>
          ) : (
            registration.resale.has_pending_waitlist_invitation
              ? t('waitlist_invitation_sent_warning')
              : t('resale_in_progress_warning')
          )}
        </UI.Warning>
      )}
      {preregistration?.lottery_status === LotteryStatus.selected && preregistration.draw_result?.end_date && (
        <UI.Success sc={{ relative: true, mt: 2 }} icon={null} style={{ overflow: 'hidden', zIndex: 300 }}>
          <img
            src={assetUrl('images/confetti.svg')}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'center',
              zIndex: 1,
              opacity: 0.15,
            }}
            alt={t('lottery_selected_title')}
          />
          <UI.Div style={{ position: 'relative', zIndex: 2 }}>
            <UI.H5>
              {t('lottery_selected_title')}
            </UI.H5>
            <UI.Paragraph sc={{ mb: 1 }}>
              <Trans
                i18nKey={preregistration.draw_result.amount > 0
                  ? 'lottery_selected_description'
                  : 'lottery_selected_description_free'}
                values={{
                  timeLeft: hoursLeftForLotteryConfirmation > 24
                    ? t('n_days', {
                      count: daysLeftForLotteryConfirmation,
                      value: formatNumber(daysLeftForLotteryConfirmation),
                    })
                    : (minutesLeftForLotteryConfirmation > 60
                      ? t('n_hours', {
                        count: hoursLeftForLotteryConfirmation,
                        value: formatNumber(hoursLeftForLotteryConfirmation),
                      })
                      : t('n_minutes', {
                        count: minutesLeftForLotteryConfirmation,
                        value: formatNumber(minutesLeftForLotteryConfirmation),
                      })),
                  date: formatDate(parseDate(preregistration.draw_result.end_date, { timezone: 'UTC' })),
                }}
              >
                <UI.Strong />
              </Trans>
            </UI.Paragraph>
            <UI.Button
              onClick={() => setAction('confirm_lottery')}
              sc={{ brand: 'success', size: 'sm', disabled: activeAction === 'confirm_lottery' }}
            >
              {t('confirm_lottery')}
            </UI.Button>
          </UI.Div>
        </UI.Success>
      )}
      {preregistration?.lottery_status === LotteryStatus.expired && (
        <UI.Warning sc={{ relative: true, mt: 2 }} style={{ zIndex: 300 }}>
          <UI.H5>
            {t('lottery_expired_title')}
          </UI.H5>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('lottery_expired_description')}
          </UI.Paragraph>
        </UI.Warning>
      )}
      {preregistration?.lottery_status === LotteryStatus.rejected && (
        <UI.Warning sc={{ relative: true, mt: 2 }} style={{ zIndex: 300 }}>
          <UI.H5>
            {t('lottery_rejected_title')}
          </UI.H5>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('lottery_rejected_description')}
          </UI.Paragraph>
        </UI.Warning>
      )}
      {waitlistInvite?.pending_invite && (
        <UI.Success sc={{ relative: true, mt: 2 }} icon={null} style={{ overflow: 'hidden', zIndex: 300 }}>
          <img
            src={assetUrl('images/confetti.svg')}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'center',
              zIndex: 1,
              opacity: 0.15,
            }}
            alt={t('waitlist_invited_title')}
          />
          <UI.Div style={{ position: 'relative', zIndex: 2 }}>
            <UI.H5>
              {t('waitlist_invited_title')}
            </UI.H5>
            <UI.Paragraph sc={{ mb: 1 }}>
              <Trans
                i18nKey="waitlist_invited_description"
                values={{
                  timeLeft: hoursLeftForWaitlistInviteReply > 24
                    ? t('n_days', {
                      count: daysLeftForWaitlistInviteReply,
                      value: formatNumber(daysLeftForWaitlistInviteReply),
                    })
                    : (minutesLeftForWaitlistInviteReply > 60
                      ? t('n_hours', {
                        count: hoursLeftForWaitlistInviteReply,
                        value: formatNumber(hoursLeftForWaitlistInviteReply),
                      })
                      : t('n_minutes', {
                        count: minutesLeftForWaitlistInviteReply,
                        value: formatNumber(minutesLeftForWaitlistInviteReply),
                      })),
                  date: formatDate(parseDate(waitlistInvite.pending_invite.expires_at, { timezone: 'UTC' })),
                }}
              >
                <UI.Strong />
              </Trans>
            </UI.Paragraph>
            <UI.Button
              onClick={() => setAction('respond_to_waitlist_invite')}
              sc={{ brand: 'success', size: 'sm', active: activeAction === 'respond_to_waitlist_invite' }}
            >
              {t('accept_reject')}
            </UI.Button>
          </UI.Div>
        </UI.Success>
      )}
      {actions.length > 1 && (
        <>
          <UI.HR sc={{ dashed: true, my: isExpanded ? 3 : 2 }} />
          <UI.FlexContainer>
            <UI.FlexContainer
              sc={{
                relative: true,
                // Negative margin to create a 'hitbox' around the button
                // that prevents accidental toggling of the dropdown section.
                my: -1,
                ml: -1,
                mr: -1,
                pt: 0.5,
                pr: 0.5,
                pb: 1,
                pl: 1,
              }}
              style={{ zIndex: 300 }}
            >
              {actions.map((action) => (
                <UI.Button
                  onClick={() => setAction(action === activeAction ? null : action)}
                  sc={{
                    brand: primaryActions.includes(action) ? 'secondary' : undefined,
                    mt: [0.5, 1],
                    mr: [0.5, 1],
                    size: 'sm',
                    active: (activeAction === action) || (activeAction === 'edit_registration' && action === 'details'),
                  }}
                  key={action}
                >
                  {getShortActionTitle(action, t)}
                </UI.Button>
              ))}
            </UI.FlexContainer>
          </UI.FlexContainer>
        </>
      )}
    </UI.Div>
  );
};

const DownloadTicketButton = styled(UI.A)`
  ${({ theme }) => css`
    display: inline-block;
    padding: ${theme.gutter * 0.25}px;
    border-radius: ${theme.borderRadiuses.md}px;
    border: 1px solid ${theme.colors.gray[200]};
    max-width: 150px;
    color: ${theme.colors.gray[800]};
    transition: all 0.15s ease-in-out;

    &:hover {
      background: ${theme.colors.gray[50]};
      border-color: ${theme.colors.gray[400]};
    }
  `}
`;

interface ActionableRegistration {
  purchase: { batch_size: number; }
  assigned: boolean;
}

const getShortActionTitle = (action: Action, t: TFunction) => {
  if (action === 'exchange_ticket') {
    return t('exchange');
  }

  if (action === 'assign_ticket') {
    return t('assign');
  }

  if (action === 'reassign_ticket') {
    return t('reassign');
  }

  if (action === 'resell_ticket') {
    return t('sell');
  }

  if (action === 'confirm_lottery') {
    return t('confirm');
  }

  if (action === 'respond_to_waitlist_invite') {
    return t('accept_reject');
  }

  return t(action);
};

const getLongActionTitle = (action: Action, registration: ActionableRegistration, t: TFunction) => {
  if (action === 'resell_ticket' && registration.purchase && registration.purchase.batch_size > 1) {
    return t('resell_group_ticket');
  }

  return t(action);
};

export default RegistrationDetails;
